/**
 * EditableNode — one reconstructed redline segment in the editing view.
 *
 * Renders the node's current content with its decision/edit state encoding
 * (`segmentVisual`), a state badge, and — for editable segments under an
 * editing role — hover controls (改 / 删 / 下方插入). "改" swaps the row for
 * an inline `NodeTextEditor`; insert-below reveals an `InsertNodeControl`.
 */
import { useState } from 'react';

import type { EditingRun, RedlineSegment } from '../../lib/veridoc-api';

import { InsertNodeControl, type InsertNodeType } from './InsertNodeControl';
import { NodeTextEditor } from './NodeTextEditor';
import { segmentVisual } from './segmentVisual';

interface EditableNodeProps {
  segment: RedlineSegment;
  /** Position among clean (positive) nodes; null for non-clean (delete) rows. */
  cleanIndex: number | null;
  canEdit: boolean;
  busy: boolean;
  onSetText: (nodeId: string, text: string) => void;
  onDelete: (nodeId: string) => void;
  onInsertBelow: (cleanIndex: number, text: string, type: InsertNodeType) => void;
}

function runsToText(runs: EditingRun[]): string {
  return runs.map((r) => r.text).join('');
}

function bodyClass(nodeType: string, struck: boolean, muted: boolean): string {
  const base =
    nodeType === 'heading' || nodeType === 'clauseHeading'
      ? 'text-lg font-semibold text-ink-900'
      : 'font-serif text-md leading-relaxed text-ink-900';
  const decoration = [
    struck ? 'line-through' : '',
    muted ? 'text-ink-400' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return `${base} ${decoration}`.trim();
}

export function EditableNode({
  segment,
  cleanIndex,
  canEdit,
  busy,
  onSetText,
  onDelete,
  onInsertBelow,
}: EditableNodeProps): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [inserting, setInserting] = useState(false);
  const visual = segmentVisual(segment.op, segment.author);
  const editable = canEdit && segment.editable;
  const text = runsToText(segment.runs);
  const isListItem = segment.nodeType === 'listItem';

  return (
    <div className={`group relative py-1 ${visual.containerClass}`}>
      <div className="flex items-start gap-3">
        {/* State badge */}
        <span
          className={`mt-1 inline-flex shrink-0 select-none items-center rounded-sm px-1.5 py-0.5 text-[11px] font-medium ${visual.badgeClass}`}
        >
          {visual.badge}
        </span>

        {/* Body / inline editor */}
        <div className="min-w-0 flex-1">
          {editing ? (
            <NodeTextEditor
              initialText={text}
              busy={busy}
              onSave={(next) => {
                onSetText(segment.nodeId, next);
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <p className={`${bodyClass(segment.nodeType, visual.struck, visual.muted)} ${isListItem ? "ml-5 before:mr-2 before:content-['•']" : ''}`}>
              {text || <span className="text-ink-300">（空节点）</span>}
            </p>
          )}

          {inserting && (
            <div className="mt-2">
              <InsertNodeControl
                busy={busy}
                label="在此下方插入"
                onInsert={(value, type) => {
                  if (cleanIndex !== null) onInsertBelow(cleanIndex, value, type);
                  setInserting(false);
                }}
              />
            </div>
          )}
        </div>

        {/* Hover controls */}
        {editable && !editing && (
          <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-fast group-hover:opacity-100 focus-within:opacity-100">
            <ControlButton label="改" title="改写文字" onClick={() => setEditing(true)} disabled={busy} />
            <ControlButton label="删" title="删除节点" onClick={() => onDelete(segment.nodeId)} disabled={busy} danger />
            {cleanIndex !== null && (
              <ControlButton
                label="＋"
                title="在下方插入节点"
                onClick={() => setInserting((v) => !v)}
                disabled={busy}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ControlButton({
  label,
  title,
  onClick,
  disabled,
  danger,
}: {
  label: string;
  title: string;
  onClick: () => void;
  disabled: boolean;
  danger?: boolean;
}): React.JSX.Element {
  const tone = danger
    ? 'text-semantic-danger hover:bg-semantic-dangerBg'
    : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`inline-flex h-6 w-6 items-center justify-center rounded-sm border border-ink-200 bg-ink-0 text-xs ${tone} disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {label}
    </button>
  );
}

export default EditableNode;
